import { useCallback, useEffect, useState } from "react";
import api, { API, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DatabaseBackup, RotateCcw, Save, Download, Upload } from "lucide-react";

const KIND = { manual: "ruční", auto: "automatická", pre_restore: "před obnovou", uploaded: "nahraná" };
const fmt = (iso) => new Date(iso).toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "short" });

export default function BackupsCard() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [restore, setRestore] = useState(null);
  const [confirm, setConfirm] = useState("");
  const load = useCallback(() => api.get("/backups").then((r) => setList(r.data)).catch((e) => toast.error(apiErr(e))), []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    setBusy(true);
    try { await api.post("/backups", { note }); toast.success("Záloha vytvořena"); setNote(""); load(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const doRestore = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/backups/${restore.id}/restore`, { confirm });
      toast.success(`Obnoveno ${Object.values(data.restored).reduce((a, b) => a + b, 0)} záznamů ze zálohy ${fmt(restore.created_at)}`);
      setRestore(null); setConfirm(""); load(); setTimeout(() => window.location.reload(), 1200);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await api.post("/backups/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Záloha nahrána – nyní ji můžete obnovit"); setOpen(true); load(); }
    catch (err) { toast.error(apiErr(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const last = list[0];
  return (
    <div className="bg-white rounded-xl border p-5" data-testid="backups-card">
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="font-semibold text-[#0A2540] flex items-center gap-2"><DatabaseBackup className="h-4 w-4" /> Zálohy systému</h3>
        <span className="text-sm text-slate-500" data-testid="backups-last">{last ? `Poslední: ${fmt(last.created_at)} · ${last.total_docs} záznamů` : "Zatím žádná záloha"} · denně automaticky ve 4:00</span>
        <div className="ml-auto flex items-center gap-2">
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="poznámka (volitelné)" className="h-9 w-48" data-testid="backup-note" />
          <Button size="sm" onClick={create} disabled={busy} style={{ background: "#0A2540" }} data-testid="backup-create"><Save className="h-4 w-4 mr-1" /> Zálohovat teď</Button>
          <Button size="sm" variant="outline" onClick={() => setOpen(!open)} data-testid="backups-toggle">{open ? "Skrýt" : `Historie (${list.length})`}</Button>
          <label className="inline-flex items-center h-9 px-3 rounded-md border text-sm cursor-pointer hover:bg-slate-50" data-testid="backup-upload-label">
            <Upload className="h-4 w-4 mr-1" /> Nahrát soubor
            <input type="file" accept=".gz,.bson.gz,application/gzip" className="hidden" data-testid="backup-upload-input" onChange={upload} />
          </label>
        </div>
      </div>
      {open && (
        <div className="mt-4 divide-y border rounded-lg" data-testid="backups-list">
          {list.map((b) => (
            <div key={b.id} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm" data-testid={`backup-${b.id}`}>
              <span className="font-medium text-[#0A2540] w-32">{fmt(b.created_at)}</span>
              <span className="text-slate-600 flex-1 min-w-0 truncate">{b.note}</span>
              <span className="text-xs text-slate-400">{KIND[b.kind] || b.kind} · {b.actor} · {b.total_docs} záznamů · {Math.round(b.size / 1024)} kB</span>
              <a href={`${API}/backups/${b.id}/download`} className="inline-flex items-center h-8 px-2 rounded border text-xs hover:bg-slate-50" data-testid={`backup-download-${b.id}`}><Download className="h-3 w-3 mr-1" /> Stáhnout</a>
              <Button size="sm" variant="outline" className="h-8 text-red-700 border-red-300" onClick={() => { setRestore(b); setConfirm(""); }} data-testid={`backup-restore-${b.id}`}><RotateCcw className="h-3 w-3 mr-1" /> Obnovit</Button>
            </div>
          ))}
          {list.length === 0 && <p className="p-3 text-sm text-slate-500">Žádné zálohy.</p>}
        </div>
      )}
      <Dialog open={!!restore} onOpenChange={(o) => !o && setRestore(null)}>
        <DialogContent data-testid="restore-dialog">
          <DialogHeader><DialogTitle className="text-red-700">Obnovit systém ze zálohy</DialogTitle></DialogHeader>
          {restore && <p className="text-sm text-slate-600">Celý systém (SVJ, byty, rezervace, protokoly, uživatelé, akce) se vrátí do stavu z <b>{fmt(restore.created_at)}</b> ({restore.note}). Vše, co vzniklo později, bude přepsáno. Před obnovou se automaticky uloží záloha aktuálního stavu, takže krok lze vrátit.</p>}
          <div className="space-y-1"><Label className="text-xs">Pro potvrzení napište <b>OBNOVIT</b></Label><Input value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="restore-confirm" /></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRestore(null)}>Zrušit</Button>
            <Button variant="destructive" disabled={busy || confirm.trim().toUpperCase() !== "OBNOVIT"} onClick={doRestore} data-testid="restore-submit"><RotateCcw className="h-4 w-4 mr-1" /> Obnovit ze zálohy</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
